import { Trans } from '@lingui/macro'

import * as React from 'react'

import { useFormContext, useWatch } from '~shared/segments/Form'
import { ButtonWithBack } from '~ui/components/buttons/ButtonWithBack/ButtonWithBack'
import { Text } from '~ui/components/typography/Text/Text'

import { TOrderForm, useFormErrorHandlerCtx } from '../contexts/FormProvider'

type TProps = NoChildren & {
  onSuccess: () => void
  backToMenu: () => void
}

// the fields that are on the first part of the FormScreen
// and that should be validated before moving to the next section
const firstMobileSectionFields: RoA<keyof TOrderForm> = [
  `givenName`,
  `familyName`,
  `phonePrefix`,
  `phoneNumber`,
  `email`,
  `note`,
  `confirmedAgreements`,
]

/**
 * Button for validating the first section of the mobile order form.
 * If all of the fields are valid, the `onSuccess` callback will be called.
 * If not, errors are automatically propagated to invalid fields
 * and the fields are watched & revalidated onChange (to hide the errors).
 */
export const ValidateContactFormButton: React.FC<TProps> = ({ onSuccess, backToMenu }) => {
  const [hasValidated, setHasValidated] = React.useState(false)
  const [hasError, setHasError] = React.useState(false)

  const { control, trigger: validate } = useFormContext<TOrderForm>()
  const handleFormError = useFormErrorHandlerCtx()

  // as stated here https://react-hook-form.com/api/useform
  // when the RHF mode is set to `onSubmit`, the `handleSubmit` will connect `onChange` re-validations on all invalid fields
  // but since the validate (trigger) is used here rather than `handleSubmit` (which would validate whole field & present unnecessary errors on the next page)
  // those onChange re-validations are not connected here
  // -> but we can watch them manually!

  const firstMobileSectionValues = useWatch({
    control,
    disabled: !hasValidated,
    name: firstMobileSectionFields,
  })

  // validate form on click and call the onSuccess callback
  const validateForm = React.useCallback(async () => {
    const firstSectionValid = await validate(firstMobileSectionFields)

    setHasValidated(true)

    if (!firstSectionValid) {
      setHasError(true)
      return
    }

    setHasError(false)
    onSuccess()
  }, [onSuccess, validate])

  // re-validate the fields on any value change if the section was already validated via the button
  // (to hide the potential error messages as they are fixed)
  React.useEffect(() => {
    if (!hasValidated) {
      return
    }

    validate(firstMobileSectionFields)
  }, [validate, firstMobileSectionValues, hasValidated])

  // if there was an error during the validation via the button, scroll to the invalid field
  React.useEffect(() => {
    if (!hasError) {
      return
    }

    handleFormError()
    setHasError(false)
  }, [hasError, handleFormError])

  return (
    <ButtonWithBack
      buttonSize={`large`}
      onClick={validateForm}
      onBackClick={backToMenu}
      bgColor={`conversion`}
    >
      <Text fontStyle={`upperCase`}>
        <Trans>continue</Trans>
      </Text>
    </ButtonWithBack>
  )
}
